import { NgModule } from '@angular/core';
import { RouterModule, Routes, PreloadAllModules } from '@angular/router';
import { featureAvailabilityGuard, UNAVAILABLE_ROUTES } from '@eredes/auth';

import { Auth401Component } from './auth/401/401.component';
import { AuthGuard } from './auth/auth-guard.service';
import { AuthProfileGuard } from './auth/auth-profile.guard';
import { AuthRequestGuard } from './auth/auth-request-guard.service';
import { AuthComponent } from './auth/auth.component';
import { UserRecoveryComponent } from './auth/user-recovery/user-recovery.component';
import { UserVerifyComponent } from './auth/user-verify/user-verify.component';
import { RequestClarificationComponent } from './request/request-clarification/request-clarification.component';
import { RequestCpeFilterComponent } from './request/request-cpe/cpe-filter/cpe-filter.component';
import { RequestCpeComponent } from './request/request-cpe/request-cpe.component';
import { RequestCreateFormComponent } from './request/request-create/request-create-form/request-create-form.component';
import { RequestCreateComponent } from './request/request-create/request-create.component';
import { RequestDefaultComponent } from './request/request-default/request-default.component';
import { RequestDetailComponent } from './request/request-detail/request-detail.component';
import { RequestDetailOldComponent } from './request/request-detail-old/request-detail-old.component';
import { RequestHistoryComponent } from './request/request-history/request-history.component';
import { RequestListComponent } from './request/request-list/request-list.component';
import { RequestTypePermissions } from './request/request-list/request-type-permissions.model';
import { RequestListOldComponent } from './request/request-list-old/request-list-old.component';
import { RequestListOrdComponent } from './request/request-list-ord/request-list-ord.component';
import { RequestComponent } from './request/request.component';
import { RequestJumpDetailComponent } from './request-form/containers/request-jump-detail/request-jump-detail.component';
import { RequestListJumpComponent } from './request-form/containers/request-list-jump/request-list-jump.component';

const appRoutes: Routes = [
  { path: '', redirectTo: '/login', pathMatch: 'full' },
  { path: 'login', component: AuthComponent },
  { path: '401', component: Auth401Component },
  {
    path: 'login-recover',
    loadComponent: () =>
      import('./auth/recover-password/recover-password.component').then((c) => c.RecoverPasswordComponent),
    canActivate: [featureAvailabilityGuard],
  },
  {
    // route "ordconsumer" seems to be unused
    path: 'ordconsumer',
    loadComponent: () =>
      import('./auth/recover-password/recover-password.component').then((c) => c.RecoverPasswordComponent),
    canActivate: [featureAvailabilityGuard],
    data: { messagingProfiles: ['ordconsumer'] },
  },
  { path: 'user-recovery/:token', component: UserRecoveryComponent, canActivate: [featureAvailabilityGuard] },
  { path: 'user-verify/:id/:token', component: UserVerifyComponent, canActivate: [featureAvailabilityGuard] },
  {
    path: 'home',
    loadChildren: () => import('./home/home.module').then((m) => m.HomeModule),
    canActivate: [AuthGuard, featureAvailabilityGuard],
  },
  {
    path: 'schedule',
    loadChildren: () => import('./schedule/schedule.module').then((m) => m.ScheduleModule),
    canActivate: [AuthGuard, featureAvailabilityGuard],
  },
  {
    path: 'messaging',
    loadChildren: () => import('./messaging/messaging.module').then((m) => m.MessagingModule),
    canActivate: [AuthGuard, featureAvailabilityGuard],
    data: {
      expectedPermissions: ['messaging_write', 'messaging_productor_write'],
    },
  },
  {
    path: 'contract',
    loadChildren: () => import('./contract-manager/contract-manager.module').then((m) => m.ContractManagerModule),
    canActivate: [AuthGuard, featureAvailabilityGuard],
  },
  {
    path: 'interlocutors',
    loadChildren: () => import('./contacts/contacts.routes').then((c) => c.CONTACTS_ROUTES),
    canActivate: [AuthGuard, featureAvailabilityGuard],
  },
  {
    path: 'billing',
    loadChildren: () => import('./billing/billing.module').then((m) => m.BillingModule),
    canActivate: [AuthGuard, featureAvailabilityGuard],
  },
  {
    path: 'energy',
    loadChildren: () => import('./energy/energy.routes').then((c) => c.ENERGY_ROUTES),
    canActivate: [AuthGuard, featureAvailabilityGuard],
  },
  {
    path: 'request/request-edp',
    component: RequestClarificationComponent,
    canActivate: [AuthRequestGuard, featureAvailabilityGuard],
    data: {
      expectedPermissions: RequestTypePermissions.getAllRequestPermissions(),
    },
    children: [
      {
        path: '',
        component: RequestListOldComponent,
        canActivate: [featureAvailabilityGuard],
        data: { requestOperationType: '', requestTitle: '' },
      },
      {
        path: 'request-detail/:id',
        component: RequestDetailOldComponent,
        canActivate: [featureAvailabilityGuard],
        data: { requestType: '', requestOperationType: '', requestTitle: '' },
      },
    ],
  },
  {
    path: 'request',
    component: RequestComponent,
    canActivate: [AuthRequestGuard, featureAvailabilityGuard],
    data: {
      expectedPermissions: {
        history: RequestTypePermissions.getAllRequestPermissions(),
        default: [...RequestTypePermissions.getAllRequestPermissions(), 'information_cpe'],
      },
    },
    children: [
      {
        path: 'solicitation',
        component: RequestDefaultComponent,
        canActivate: [AuthGuard, featureAvailabilityGuard],
        data: {
          expectedPermissions: RequestTypePermissions.getAllRequestPermissions(),
          requestTitle: 'Pedidos',
        },
        children: [
          { path: '', component: RequestListJumpComponent },
          { path: 'detail/:id', component: RequestJumpDetailComponent },
        ],
      },
      {
        path: 'history',
        component: RequestHistoryComponent,
        canActivate: [AuthGuard, featureAvailabilityGuard],
        data: {
          expectedPermissions: RequestTypePermissions.getAllRequestPermissions(),
          requestTitle: 'Histórico',
        },
        children: [
          {
            path: '',
            component: RequestListComponent,
            canActivate: [featureAvailabilityGuard],
            data: {
              requestOperationType: 'pt.edp.request.operation.1,pt.edp.request.operation.2',
              requestTitle: 'Histórico',
            },
          },
          {
            path: 'detail/:id',
            component: RequestDetailComponent,
            canActivate: [featureAvailabilityGuard],
            data: {
              requestOperationType: 'pt.edp.request.operation.1,pt.edp.request.operation.2',
            },
          },
        ],
      },
      {
        path: 'request-cpe',
        component: RequestCpeComponent,
        canActivate: [AuthGuard, featureAvailabilityGuard],
        data: {
          expectedPermissions: ['information_cpe'],
        },
        children: [{ path: '', component: RequestCpeFilterComponent }],
      },
      {
        path: 'list',
        component: RequestListOrdComponent,
        canActivate: [featureAvailabilityGuard],
        data: {
          expectedPermissions: RequestTypePermissions.getAllRequestPermissions(),
        },
        children: [
          {
            path: '',
            component: RequestListComponent,
            canActivate: [featureAvailabilityGuard],
            data: {
              requestOperationType: 'pt.edp.request.operation.1,pt.edp.request.operation.2',
              requestTitle: '',
            },
          },
          {
            path: 'detail/:id',
            component: RequestDetailComponent,
            canActivate: [featureAvailabilityGuard],
            data: {
              requestType: '',
              requestOperationType: 'pt.edp.request.operation.1,pt.edp.request.operation.2',
              requestTitle: '',
            },
          },
        ],
      },
    ],
  },
  {
    path: 'request/create',
    component: RequestCreateComponent,
    canActivate: [AuthGuard, featureAvailabilityGuard],
    data: {
      requestType: 'o',
      requestOperationType: 'pt.edp.request.operation.1',
      requestTitle: 'Operação',
      expectedPermissions: RequestTypePermissions.getCreatePoPermissions(),
    },
  },
  {
    path: 'user',
    loadChildren: () => import('./user/user.module').then((m) => m.UserModule),
    canActivate: [featureAvailabilityGuard],
  },
  {
    path: 'notifications',
    loadChildren: () => import('./notifications/notifications.routes').then((c) => c.NOTIFICATIONS_ROUTES),
    canActivate: [AuthGuard, featureAvailabilityGuard],
  },
  {
    path: 'notifications-config',
    loadChildren: () =>
      import('./notifications-config/notifications-config.module').then((m) => m.NotificationsConfigModule),
    canActivate: [AuthGuard, featureAvailabilityGuard],
  },
  {
    path: 'documents',
    loadChildren: () => import('./documents/documents.module').then((m) => m.DocumentsModule),
    canActivate: [AuthGuard, featureAvailabilityGuard],
  },
  {
    path: 'queries',
    loadChildren: () => import('./queries/queries.module').then((m) => m.QueriesModule),
    canActivate: [AuthGuard, featureAvailabilityGuard],
    data: {
      expectedPermissions: ['queries_selfconsumption_write'],
    },
  },
  {
    path: 'request/create/:formType/:formAlias',
    canActivate: [AuthRequestGuard, featureAvailabilityGuard],
    component: RequestCreateFormComponent,
  },
  {
    path: 'user-account',
    loadChildren: () => import('./user-account/user-account.module').then((m) => m.UserAccountModule),
    canActivate: [AuthGuard, AuthProfileGuard, featureAvailabilityGuard],
  },
  {
    path: 'cookie-policy',
    loadChildren: () => import('./cookie-policy/cookie-policy.module').then((m) => m.CookiePolicyModule),
  },
  ...UNAVAILABLE_ROUTES,
];

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      enableTracing: false,
      preloadingStrategy: PreloadAllModules,
      bindToComponentInputs: true,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
